/**
 * Generic student-import parsing + column-mapping helpers.
 *
 * Supports CSV and XLSX (.xlsx) spreadsheets. XLSX is unzipped in the browser
 * with `fflate` (already a transitive dep) and the worksheet XML is parsed with
 * the platform DOMParser — no new heavyweight dependency required.
 *
 * The flow the import modal drives:
 *   1. parseSpreadsheet(file)  -> { headers, dataRows }
 *   2. autoMapHeaders(headers) -> string[] (one field key per column)
 *   3. buildRows(dataRows, headers, mapping) -> { rows, validCount, invalidCount }
 *
 * Columns the center has no matching system field for are mapped to the special
 * `note` key: each becomes a note ({ title: header, content: cell value }) that
 * the backend persists as a proposal, keeping the data and making it filterable.
 */
import { unzipSync, strFromU8 } from 'fflate';

// Special, non-field mapping targets.
export const FIELD_IGNORE = 'ignore';
export const FIELD_NOTE = 'note';
export const FIELD_FULL_NAME = 'fullName';

// Known system fields a column can map to (besides fullName / note / ignore).
export const NAME_FIELDS = ['firstName', 'middleName', 'lastName'];
export const SIMPLE_FIELDS = [
  'phone', 'parentPhone', 'email', 'nationalId',
  'gender', 'dateOfBirth', 'city', 'address',
];

// Ordered list of selectable targets for the mapping dropdown.
export const MAPPABLE_FIELDS = [
  FIELD_FULL_NAME,
  ...NAME_FIELDS,
  ...SIMPLE_FIELDS,
  FIELD_NOTE,
  FIELD_IGNORE,
];

/**
 * Heuristic header -> field matchers, evaluated in order (first match wins).
 * Covers Arabic, Hebrew and English headers. Parent/guardian phone is checked
 * before plain phone so "هاتف ولي الأمر" doesn't match as the student's phone.
 */
const HEADER_MATCHERS = [
  [/الاسم الأول|الأسم الأول|الإسم الأول|first\s*name|שם\s*פרטי/i, 'firstName'],
  [/اسم العائلة|اسم العايلة|إسم العائلة|last\s*name|family\s*name|שם\s*משפחה/i, 'lastName'],
  [/الاسم الأوسط|الاسم الاوسط|middle\s*name|اسم الأب|اسم الاب/i, 'middleName'],
  [/(ولي|أهل|اهل|parent|guardian|הורה).*(هاتف|جوال|تلفون|موبايل|phone|mobile|טלפון|رقم)|(هاتف|phone|mobile|رقم).*(ولي|أهل|اهل|parent|guardian|הורה)/i, 'parentPhone'],
  [/بريد|ايميل|إيميل|e-?mail|דוא"?ל/i, 'email'],
  [/هاتف|جوال|تلفون|موبايل|phone|mobile|טלפון|رقم ال?هاتف/i, 'phone'],
  [/رقم الهوية|الهوية|هوية|national\s*id|id\s*number|ת\.?ז/i, 'nationalId'],
  [/الجنس|جنس|gender|sex|מין/i, 'gender'],
  [/تاريخ الميلاد|الميلاد|ميلاد|date\s*of\s*birth|\bdob\b|birth|תאריך\s*לידה|לידה/i, 'dateOfBirth'],
  [/المدينة|مدينة|city|עיר/i, 'city'],
  [/العنوان|عنوان|address|כתובת/i, 'address'],
  // Full / multi-part name. Generic "name"/اسم falls through here last so that
  // the specific first/last matchers above take precedence.
  [/الاسم الثلاثي|الإسم الثلاثي|الاسم الرباعي|الاسم الكامل|الاسم الثنائي|اسم الطالب|اسم الطالبة|full\s*name|الاسم|الإسم|إسم|اسم|\bname\b|שם/i, FIELD_FULL_NAME],
];

/** Guess the system field for a single header string, or '' if none. */
export function autoMapHeader(header) {
  const h = String(header || '').trim();
  if (!h) return FIELD_IGNORE;
  for (const [re, field] of HEADER_MATCHERS) {
    if (re.test(h)) return field;
  }
  // No system field matched — preserve the column as a note by default.
  return FIELD_NOTE;
}

/** Map every header, but only allow a single column per unique name field. */
export function autoMapHeaders(headers) {
  const used = new Set();
  return headers.map((h) => {
    let field = autoMapHeader(h);
    // Don't auto-assign the same dedicated field twice; demote extras to notes.
    if (field !== FIELD_NOTE && field !== FIELD_IGNORE && used.has(field)) {
      field = FIELD_NOTE;
    }
    if (field !== FIELD_NOTE && field !== FIELD_IGNORE) used.add(field);
    return field;
  });
}

/**
 * Split a free-text full name into first / middle / last.
 *  - 1 token  -> firstName only
 *  - 2 tokens -> first + last
 *  - 3 tokens -> first + middle + last
 *  - 4+       -> first + middle + (everything else as last)
 */
export function splitFullName(full) {
  const tokens = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { firstName: '', middleName: '', lastName: '' };
  if (tokens.length === 1) return { firstName: tokens[0], middleName: '', lastName: '' };
  if (tokens.length === 2) return { firstName: tokens[0], middleName: '', lastName: tokens[1] };
  if (tokens.length === 3) return { firstName: tokens[0], middleName: tokens[1], lastName: tokens[2] };
  return { firstName: tokens[0], middleName: tokens[1], lastName: tokens.slice(2).join(' ') };
}

// ---------------------------------------------------------------------------
// CSV parsing
// ---------------------------------------------------------------------------

/** Parse one CSV line, honoring quoted fields and "" escaped quotes. */
function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const next = line[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') { cur += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else cur += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

/** Parse CSV text into a row grid (array of string arrays). */
export function parseCsv(text) {
  const clean = String(text || '').replace(/^﻿/, '');
  const lines = clean.split(/\r?\n/);
  const grid = [];
  for (const line of lines) {
    if (line.trim() === '') continue;
    grid.push(parseCsvLine(line));
  }
  return grid;
}

// ---------------------------------------------------------------------------
// XLSX parsing (via fflate unzip + DOMParser)
// ---------------------------------------------------------------------------

/** Convert an Excel column ref ("A","B","AA") to a 0-based index. */
export function colRefToIndex(ref) {
  const letters = String(ref).replace(/[^A-Z]/gi, '').toUpperCase();
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n - 1;
}

/** Parse a sharedStrings.xml string into an array of strings. */
export function parseSharedStrings(xml) {
  const out = [];
  if (!xml) return out;
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const siNodes = doc.getElementsByTagName('si');
  for (let i = 0; i < siNodes.length; i++) {
    // <si> may contain a single <t> or multiple <r><t> runs.
    const tNodes = siNodes[i].getElementsByTagName('t');
    let s = '';
    for (let j = 0; j < tNodes.length; j++) s += tNodes[j].textContent;
    out.push(s);
  }
  return out;
}

/**
 * Convert a worksheet XML string (+ shared strings) into a row grid, preserving
 * column positions via each cell's `r` reference. This is the core, DOM-only
 * parsing step — kept separate from the zip step so it can be unit-tested
 * without round-tripping a real .xlsx archive.
 */
export function xlsxSheetToGrid(sheetXml, sharedStrings = []) {
  const sheetDoc = new DOMParser().parseFromString(sheetXml, 'application/xml');
  const rowNodes = sheetDoc.getElementsByTagName('row');
  const grid = [];
  let maxCols = 0;

  for (let r = 0; r < rowNodes.length; r++) {
    const cellNodes = rowNodes[r].getElementsByTagName('c');
    const row = [];
    for (let c = 0; c < cellNodes.length; c++) {
      const cell = cellNodes[c];
      const ref = cell.getAttribute('r') || '';
      const colIdx = ref ? colRefToIndex(ref) : c;
      const type = cell.getAttribute('t');
      let value = '';
      if (type === 'inlineStr') {
        const tNodes = cell.getElementsByTagName('t');
        for (let j = 0; j < tNodes.length; j++) value += tNodes[j].textContent;
      } else {
        const vNode = cell.getElementsByTagName('v')[0];
        const raw = vNode ? vNode.textContent : '';
        if (type === 's') value = sharedStrings[parseInt(raw, 10)] ?? '';
        else value = raw;
      }
      row[colIdx] = String(value).trim();
    }
    // Normalize holes to empty strings.
    for (let i = 0; i < row.length; i++) if (row[i] === undefined) row[i] = '';
    if (row.length > maxCols) maxCols = row.length;
    grid.push(row);
  }

  // Pad every row to the widest row so columns line up.
  for (const row of grid) {
    while (row.length < maxCols) row.push('');
  }
  return grid;
}

/** Parse an .xlsx ArrayBuffer/Uint8Array into a row grid. */
export function parseXlsx(arrayBuffer) {
  const files = unzipSync(new Uint8Array(arrayBuffer));

  const ssFile = files['xl/sharedStrings.xml'];
  const sharedStrings = ssFile ? parseSharedStrings(strFromU8(ssFile)) : [];

  // Resolve the first worksheet's path, falling back to sheet1.xml.
  let sheetPath = 'xl/worksheets/sheet1.xml';
  if (!files[sheetPath]) {
    const found = Object.keys(files).find((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k));
    if (found) sheetPath = found;
  }
  const sheetFile = files[sheetPath];
  if (!sheetFile) return [];

  return xlsxSheetToGrid(strFromU8(sheetFile), sharedStrings);
}

// ---------------------------------------------------------------------------
// High-level entry points
// ---------------------------------------------------------------------------

const MAX_ROWS = 500;

/**
 * Parse a File (csv or xlsx) into { headers, dataRows }.
 * Drops fully-empty rows. Throws on unsupported type / empty content.
 */
export async function parseSpreadsheet(file) {
  const name = (file?.name || '').toLowerCase();
  let grid;
  if (name.endsWith('.xlsx')) {
    const buf = await file.arrayBuffer();
    grid = parseXlsx(buf);
  } else if (name.endsWith('.csv') || name.endsWith('.txt')) {
    const text = await file.text();
    grid = parseCsv(text);
  } else {
    throw new Error('UNSUPPORTED_TYPE');
  }

  // Drop fully-empty rows.
  grid = grid.filter((row) => row.some((c) => String(c ?? '').trim() !== ''));
  if (grid.length < 2) throw new Error('NO_DATA_ROWS');

  const headers = grid[0].map((h, i) => String(h ?? '').trim() || `Column ${i + 1}`);
  const dataRows = grid.slice(1);
  return { headers, dataRows };
}

/**
 * Apply a column->field mapping to data rows and produce import-ready records.
 * `mapping[colIndex]` is a field key from MAPPABLE_FIELDS.
 * Returns { rows, validCount, invalidCount, maxRowsExceeded }.
 */
export function buildRows(dataRows, headers, mapping) {
  const rows = dataRows.slice(0, MAX_ROWS).map((cells) => {
    const rec = {
      firstName: '', middleName: '', lastName: '',
      phone: '', parentPhone: '', email: '', nationalId: '',
      gender: '', dateOfBirth: '', city: '', address: '',
      notes: [],
    };
    let fullName = '';
    const explicit = { firstName: false, middleName: false, lastName: false };

    mapping.forEach((field, colIdx) => {
      const value = String(cells[colIdx] ?? '').trim();
      if (!field || field === FIELD_IGNORE) return;
      if (field === FIELD_NOTE) {
        if (value) rec.notes.push({ title: headers[colIdx] || 'ملاحظة', content: value });
        return;
      }
      if (field === FIELD_FULL_NAME) {
        if (value) fullName = value;
        return;
      }
      if (NAME_FIELDS.includes(field)) {
        if (value) { rec[field] = value; explicit[field] = true; }
        return;
      }
      // Simple field — last non-empty value wins.
      if (value) rec[field] = value;
    });

    // Resolve name: split full name, then let explicit columns override.
    if (fullName) {
      const split = splitFullName(fullName);
      if (!explicit.firstName) rec.firstName = split.firstName;
      if (!explicit.middleName) rec.middleName = split.middleName;
      if (!explicit.lastName) rec.lastName = split.lastName;
    }

    rec.__valid = rec.firstName.trim().length >= 1;
    return rec;
  });

  const validCount = rows.filter((r) => r.__valid).length;
  return {
    rows,
    validCount,
    invalidCount: rows.length - validCount,
    maxRowsExceeded: dataRows.length > MAX_ROWS,
  };
}

/**
 * Duplicate key for a phone — mirrors the backend's normalizePhone so
 * "0501234567", "+972 50-123-4567" and "501234567" (Excel ate the zero) collide.
 */
export function phoneKey(raw) {
  return String(raw ?? '').replace(/\D/g, '').replace(/^972/, '').replace(/^0/, '');
}

/**
 * Indexes of rows whose phone already appeared on an earlier row. The backend
 * skips these; the preview flags them so the user isn't surprised.
 */
export function findInFilePhoneDuplicates(rows) {
  const seen = new Set();
  const dups = new Set();
  (rows || []).forEach((r, i) => {
    const key = phoneKey(r?.phone);
    if (!key) return;
    if (seen.has(key)) dups.add(i);
    else seen.add(key);
  });
  return dups;
}

/** True if the mapping yields at least one name source (full or first name). */
export function mappingHasName(mapping) {
  return mapping.some((f) => f === FIELD_FULL_NAME || f === 'firstName');
}
